import { ApplicationConfig, inject } from '@angular/core';
import {
    NavigationError,
    provideRouter,
    RedirectCommand,
    UrlSegmentGroup,
    UrlSerializer,
    withInMemoryScrolling,
    withNavigationErrorHandler,
} from '@angular/router';
import { chargeyeShellRoute } from '@kempower-nx-workspace/chargeye-shell';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAuth, unauthorizedInterceptor, AUTH0_REST_API_URL } from '@kempower-nx-workspace/auth0-authentications';
import { environment, provideAppConfig, AUTH_STORAGE_KEY, LOGOUT_STORAGE_CLEAR_KEYS } from '@kempower-nx-workspace/ce-environment';
import { provideChargeEyeApiConfig } from '@kempower-nx-workspace/ce-api-config';
import { provideDataTable } from '@kempoweroyj/ds-table';
import { LicensingApiServiceObservable } from '@kempoweroyj/ngx-licensing-api-client';
import { ContentApiServiceObservable } from '@kempoweroyj/ngx-content-api-client';
import { ErrorCodesApiServiceObservable } from '@kempoweroyj/ngx-error-codes-api-client';
import { EvseApiServiceObservable } from '@kempoweroyj/ngx-evse-api-client';
import { CONTENT_CDN_CONFIG } from '@kempower-nx-workspace/chargeye-content';
import { ALERT_DETAILS_SHOW_TECHNICAL_INFO } from '@kempower-nx-workspace/chargeye-station-alerts';
import { PermissionsInfoService } from '@kempower-nx-workspace/chargeye-licensing';

const { appConfig: envConfig } = environment;

/** Recursively drop any `dialog` named outlet from a URL segment group; returns true if one was removed. */
function stripDialogOutlet(group: UrlSegmentGroup): boolean {
    let stripped = false;
    if (group.children['dialog']) {
        delete group.children['dialog'];
        stripped = true;
    }
    for (const key of Object.keys(group.children)) {
        if (stripDialogOutlet(group.children[key])) {
            stripped = true;
        }
    }
    return stripped;
}

/**
 * Recover from a navigation that failed only because of an orphaned `(dialog:…)` aux outlet —
 * e.g. a stale dialog link, or a dialog URL whose primary path isn't the station route the
 * dialog is scoped to. Strip the dialog outlet and redirect; the primary path then resolves
 * normally (or falls through to the 404 route) instead of leaving a blank page. Other navigation
 * errors are left untouched.
 */
function recoverFromOrphanedDialogOutlet(error: NavigationError): RedirectCommand | undefined {
    if (!error.url) return undefined;
    const serializer = inject(UrlSerializer);
    const tree = serializer.parse(error.url);
    return stripDialogOutlet(tree.root) ? new RedirectCommand(tree) : undefined;
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            chargeyeShellRoute,
            withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
            withNavigationErrorHandler(recoverFromOrphanedDialogOutlet)
        ),
        provideAnimations(),
        provideHttpClient(withInterceptors([unauthorizedInterceptor])),
        provideAppConfig(envConfig),
        provideAuth({ config: envConfig.auth, storageKey: AUTH_STORAGE_KEY, logoutStorageClear: LOGOUT_STORAGE_CLEAR_KEYS }),
        provideChargeEyeApiConfig({ appConfig: envConfig, storageKey: AUTH_STORAGE_KEY, restApiUrlToken: AUTH0_REST_API_URL }),
        provideDataTable(),
        LicensingApiServiceObservable,
        ContentApiServiceObservable,
        ErrorCodesApiServiceObservable,
        EvseApiServiceObservable,
        // Wire the alert-details dialog's technical-info gate to the real permission. The dialog
        // lives in a shared leaf lib that can't depend on chargeye-licensing, so it reads this
        // token; the app supplies the actual stream (covers both station-view + the support wizard).
        {
            provide: ALERT_DETAILS_SHOW_TECHNICAL_INFO,
            useFactory: () => inject(PermissionsInfoService).hasPermission$('fleet.chargingStation.hardware.showTechnicalInfo'),
        },
        ...(environment.contentCdn
            ? [{ provide: CONTENT_CDN_CONFIG, useValue: environment.contentCdn }]
            : []),
    ],
};
